import React, { useMemo, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography
} from "@material-ui/core";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import useBillingPurchases from "../../hooks/useBillingPurchases";

const money = cents =>
  (Number(cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });

const recurrenceLabel = {
  quarterly: "Trimestral",
  semiannual: "Semestral",
  yearly: "Anual"
};

const createBrowserIdempotencyKey = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random()
    .toString(36)
    .slice(2)}`;

const MultiTermCheckoutPage = ({ options, renewal = false }) => {
  const [billingType, setBillingType] = useState("CREDIT_CARD");
  const [installments, setInstallments] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const idempotencyKey = useRef(createBrowserIdempotencyKey());
  const { createCheckout, createRenewalCheckout, getPurchase } =
    useBillingPurchases();

  const installmentOptions = useMemo(
    () =>
      Array.from(
        { length: Number(options.maxInstallments || 1) },
        (_, index) => index + 1
      ),
    [options.maxInstallments]
  );

  const submit = async () => {
    const openedWindow = window.open("", "_blank");
    setLoading(true);
    try {
      const checkout = await (renewal
        ? createRenewalCheckout
        : createCheckout)(
        {
          planId: options.planId,
          billingType,
          selectedInstallments: billingType === "PIX" ? 1 : installments
        },
        idempotencyKey.current
      );
      setResult(checkout);
      if (checkout.checkoutUrl) {
        if (openedWindow) openedWindow.location = checkout.checkoutUrl;
        else window.open(checkout.checkoutUrl, "_blank");
      } else if (openedWindow) {
        openedWindow.close();
      }
      toast.success("Checkout criado. Conclua o pagamento na pagina do Asaas.");
    } catch (error) {
      if (openedWindow) openedWindow.close();
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!renewal || !result?.purchaseUuid) return undefined;
    let active = true;
    const timer = window.setInterval(async () => {
      try {
        const current = await getPurchase(result.purchaseUuid);
        if (!active) return;
        setResult(current);
        if (current.status === "APPROVED") {
          window.clearInterval(timer);
          toast.success("Renovação aprovada. O acesso foi atualizado.");
          window.setTimeout(() => window.location.reload(), 800);
        }
      } catch {
        // O polling é somente acompanhamento; o checkout continua válido.
      }
    }, 5000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [renewal, result?.purchaseUuid]);

  if (!options.featureEnabled) {
    return (
      <Paper style={{ padding: 24, maxWidth: 620 }}>
        <Typography variant="h6">Pagamento multi-periodo indisponivel</Typography>
        <Typography color="textSecondary" style={{ marginTop: 8 }}>
          O checkout seguro ainda nao foi habilitado neste ambiente.
        </Typography>
      </Paper>
    );
  }

  if (renewal && options.eligibility?.eligible !== true) {
    return (
      <Paper style={{ padding: 24, maxWidth: 620 }}>
        <Typography variant="h6">Renovação ainda indisponível</Typography>
        <Typography color="textSecondary" style={{ marginTop: 8 }}>
          Código: {options.eligibility?.code || "RENEWAL_NOT_ELIGIBLE"}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper style={{ padding: 24, width: "100%", maxWidth: 620 }}>
      <Typography variant="h5">
        {renewal ? "Renovar · " : ""}
        {options.planName}
      </Typography>
      <Typography color="textSecondary">
        Plano {recurrenceLabel[options.recurrence] || options.recurrence} · acesso
        por {options.termMonths} meses
      </Typography>
      <Typography variant="h4" color="primary" style={{ margin: "18px 0 4px" }}>
        {money(options.totalValueCents)}
      </Typography>
      <Typography color="textSecondary">Valor total do periodo</Typography>
      {renewal && options.eligibility && (
        <Typography color="textSecondary" style={{ marginTop: 8 }}>
          Novo período: {options.eligibility.proposedAccessBaseDate} até{" "}
          {options.eligibility.proposedAccessEndDate}. Os dias atuais são
          preservados.
        </Typography>
      )}

      <FormControl variant="outlined" fullWidth style={{ marginTop: 24 }}>
        <InputLabel>Forma de pagamento</InputLabel>
        <Select
          value={billingType}
          onChange={event => {
            setBillingType(event.target.value);
            if (event.target.value === "PIX") setInstallments(1);
          }}
          label="Forma de pagamento"
        >
          <MenuItem value="CREDIT_CARD">Cartao de credito</MenuItem>
          <MenuItem value="PIX">Pix integral</MenuItem>
        </Select>
      </FormControl>

      {billingType === "CREDIT_CARD" && (
        <FormControl variant="outlined" fullWidth style={{ marginTop: 16 }}>
          <InputLabel>Parcelas</InputLabel>
          <Select
            value={installments}
            onChange={event => setInstallments(Number(event.target.value))}
            label="Parcelas"
          >
            {installmentOptions.map(count => (
              <MenuItem key={count} value={count}>
                {count}x de aproximadamente{" "}
                {money(Math.floor(options.totalValueCents / count))}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Typography
        variant="body2"
        color="textSecondary"
        style={{ marginTop: 16 }}
      >
        Os dados do cartao serao informados somente na pagina segura do Asaas.
        Se houver resto de centavos, o provedor ajustara a ultima parcela.
      </Typography>

      <Button
        fullWidth
        variant="contained"
        color="primary"
        disabled={loading}
        onClick={submit}
        style={{ marginTop: 20 }}
      >
        {loading ? <CircularProgress size={24} /> : "Abrir checkout seguro"}
      </Button>

      {result && (
        <Typography variant="body2" style={{ marginTop: 16 }}>
          Compra {result.purchaseUuid} · status {result.status}
        </Typography>
      )}
    </Paper>
  );
};

export default MultiTermCheckoutPage;
